import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef,
  Fragment,
} from 'react';
import { isDesktop, isTablet } from 'react-device-detect';
import { round, max, min, floor, exp, pow, log2 } from 'mathjs';
import toPrettyNumber from '../../../../../functions/toPrettyNumber';
import styles from './SimpleVersion.module.css';
import SocialPopUp from './SocialPopUp/SocialPopUp';
import {
  SimplifiedChartsTop,
  SimplifiedChartsBottom,
} from './SimplifiedCharts/SimplifiedCharts';
import UserScore from './UserScore/UserScore';
import { AllInputDataContext, CalculatedDataContext } from '../../../config';
import { debounce } from 'underscore';
import { Typography, Tour } from 'antd';
import { PlantSizeSummary } from '../../controls/PlantSizeSummary';
import { ReactComponent as Map } from './icon-map.svg';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../../../index';

const { Text } = Typography;

const simplifiedCards = ['cost', 'emissions', 'security'];
const SimpleVersion = ({
  version,
  welcomeJourneyStep,
  setWelcomeJourneyStep,
  processWelcomeJourney,
}) => {
  const { simplifiedData, optionsData } = useContext(AllInputDataContext);
  const { cost, emissions, security } = simplifiedData;
  const { annualConst, reserveRemaining } = useContext(CalculatedDataContext);
  const [userScorePopup, setUserScorePopup] = useState(false);
  const [selectedCard, setSelectedCard] = useState();
  const [animateToCard, setAnimateToCard] = useState(null);

  const timeOfPopup = useRef(Date.now() - 240000);
  let simpleDataNew = { cost, emissions, security };

  simpleDataNew.cost = simpleCost();
  simpleDataNew.emissions = simpleEmissons();
  simpleDataNew.security = simpleSecurity();
  let totalAlertLevel = round(
    100 *
      (1 - pow(simpleDataNew.cost.percentage, 2)) *
      (1 - pow(simpleDataNew.emissions.percentage, 2)) *
      (1 - pow(simpleDataNew.security.percentage, 2)),
    2
  );
  const socialScorePopup = useCallback(
    debounce((totalAlertLevel) => {
      timeOfPopup.current = Date.now();
      setUserScorePopup(true);
    }, 500),
    []
  );

  useEffect(() => {
    if (animateToCard !== null) {
      setTimeout(() => {
        setSelectedCard(animateToCard);
        setAnimateToCard(null);
      }, 300);
    }
  }, [animateToCard]);

  useEffect(() => {
    if (
      totalAlertLevel > optionsData.minScoreSocialPopup &&
      timeOfPopup.current + 300000 < Date.now()
    ) {
      socialScorePopup(totalAlertLevel);
    }
  }, [optionsData.minScoreSocialPopup, socialScorePopup, totalAlertLevel]);

  useEffect(() => {
    if (selectedCard) {
      setSelectedCard(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalAlertLevel]);

  const ref1 = useRef(null);
  const ref2 = useRef(null);
  const ref3 = useRef(null);

  const steps = [
    {
      title: 'Cost',
      description: 'Keep energy costs low for consumers',
      target: () => ref1.current,
    },
    {
      title: 'Emissions',
      description: 'Keep energy emissions low for the planet',
      target: () => ref2.current,
    },
    {
      title: 'Security',
      description: 'Keep energy security high to avoid black outs',
      target: () => ref3.current,
    },
  ];

  const orderedSimplifiedTypes = Object.entries(simpleDataNew).sort(
    (a, b) => b[1].percentage - a[1].percentage
  );
  const selectedTypeName = selectedCard || orderedSimplifiedTypes[0][0];
  const selectedSimplifiedType = simpleDataNew[selectedTypeName];

  return (
    <div
      className={styles.simpleViewContainer}
      style={isTablet ? { padding: 50 } : {}}
    >
      <div
        style={{
          position: 'fixed',
          right: 0,
          botton: '9%',
          color: 'grey',
          zIndex: -1,
        }}
      >
        <Map width="100%" height="80vh" />
      </div>
      <Tour
        open={welcomeJourneyStep === 6}
        onClose={() => processWelcomeJourney()}
        mask
        type="primary"
        steps={steps}
        rootClassName="welcome-tour"
      />
      <div className={styles.topHeaderContainer}>
        <PlantSizeSummary />
        <UserScore
          userScore={round(totalAlertLevel)}
          optionsData={optionsData}
          setUserScorePopup={setUserScorePopup}
        />
      </div>
      <div className={styles.simpleChartContainer}>
        <div
          className={styles.simpleChartContainerTop}
          style={{
            ...(isDesktop && !isTablet ? { gap: 20 } : { gap: 0 }),
          }}
        >
          {simplifiedCards.map((type, index) => (
            <Fragment key={type}>
              <SimplifiedChartsTop
                myRef={index === 0 ? ref1 : index === 1 ? ref2 : ref3}
                onClick={() => setAnimateToCard(type)}
                show
                name={type}
                simpleData={simpleDataNew[type]}
                guageChartID={type}
                style={{
                  ...(type === selectedTypeName
                    ? {
                        borderColor:
                          optionsData[
                            `alertLevel${simpleDataNew[type].alertLevel}`
                          ],
                        borderWidth: '3px',
                      }
                    : {
                        animation: 'block',
                      }),
                  maxWidth: '33%',
                }}
              />
              {/* <SimplifiedChartsBottom
                onBack={() => setAnimateToCard(undefined)}
                show={selectedCard === type}
                name={type}
                simpleData={simpleDataNew[type]}
                style={{ maxWidth: '50%' }}
              /> */}
            </Fragment>
          ))}
        </div>
      </div>
      <Text>{/* <strong>Sign up to our news letter</strong> */}</Text>
      <div className={styles.detailedViewContainer}>
        <SimplifiedChartsBottom
          show
          showHeading
          name={selectedTypeName}
          simpleData={selectedSimplifiedType}
          style={{
            maxWidth: '100%',
            transition: 'all 0.3s ease',
            opacity: animateToCard === null ? 1 : 0,
          }}
        />
      </div>
      {userScorePopup && (
        <SocialPopUp
          userScore={round(totalAlertLevel)}
          optionsData={optionsData}
          setUserScorePopup={setUserScorePopup}
        />
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          marginBottom: 10,
        }}
      >
        <Text
          style={{
            position: 'absolute',
            left: 10,
            bottom: isDesktop ? 0 : 'unset',
            cursor: 'pointer',
            margin: isDesktop ? 5 : 'unset',
          }}
          onClick={() => {
            logEvent(analytics, 'restart_tour');
            setWelcomeJourneyStep(6);
          }}
        >
          Restart Tour
        </Text>
        <Text
          style={{
            position: 'absolute',
            right: 10,
            bottom: isDesktop ? 0 : 'unset',
            cursor: 'pointer',
            margin: isDesktop ? 5 : 'unset',
          }}
          onClick={() => window.location.reload()}
        >
          Restart Game
        </Text>

        <Text
          style={{ cursor: 'pointer' }}
          onClick={() => {
            logEvent(analytics, 'navigate_to_modelling_energy');
            window.open('https://modelling.energy', '_blank');
          }}
        >
          Powered by{' '}
          <span style={{ textDecoration: 'underline' }}>Modelling.Energy</span>
        </Text>
      </div>
    </div>
  );

  function simpleCost() {
    let simpleData = {};

    let costValue = annualConst.totalAnnualCost * cost.converstionFactor;

    let calculatePercentage = min(
      1,
      max(
        0,

        (costValue - cost.minValue) / (cost.maxValue - cost.minValue)
      )
    );

    simpleData.realisticValue =
      optionsData.currency +
      toPrettyNumber(round(costValue * cost.realityFactor));

    simpleData.actualValue = round(costValue, 2) + cost.displayUnits;
    simpleData.percentage = calculatePercentage;
    let imgNumber = round(simpleData.percentage * 5);
    simpleData.alertLevel = imgNumber;
    simpleData.img = {
      src: `/images/cost/${imgNumber}.jpg`,
    };

    return simpleData;
  }

  function simpleEmissons() {
    let simpleData = {};

    let emissionsValue =
      annualConst.totalAnnualCarbon * emissions.converstionFactor;

    simpleData.percentage = min(
      1,
      max(
        0,
        1 + (1 / 6) * log2((max(0, emissionsValue) + 1) / emissions.maxValue)
      )
    );

    let imgNumber = round(simpleData.percentage * 5);

    simpleData.actualValue = round(emissionsValue) + emissions.displayUnits;

    simpleData.realisticValue = (
      <>{toPrettyNumber(round(emissionsValue * 0.25 + 20))}cm</>
    );
    simpleData.alertLevel = imgNumber;
    simpleData.img = {
      src: `/images/emissions/${imgNumber}.jpg`,
    };

    return simpleData;
  }
  function simpleSecurity() {
    let simpleData = {};

    simpleData.percentage = max(
      0,

      1 -
        (reserveRemaining - security.minValue) /
          (security.maxValue - security.minValue)
    );

    const getImageAddress = () => {
      let imgNumber = reserveRemaining + 5;
      if (reserveRemaining >= 10) {
        imgNumber = 16;
      } else if (reserveRemaining >= -5) {
        imgNumber = reserveRemaining + 5;
      } else {
        imgNumber = 0;
      }

      let imgNumberBO = 'blackout' + imgNumber;
      return `/images/security/${imgNumberBO}.png`;
    };

    simpleData.img = { src: getImageAddress(), width: '', height: '200px' };

    simpleData.actualValue =
      max(-100, reserveRemaining) + security.displayUnits;

    simpleData.realisticValue =
      toPrettyNumber(
        max(
          0,
          min(8760, round(exp(2.6 - (13.67 * reserveRemaining) / 100) - 3))
        )
      ) + 'hrs';

    simpleData.percentage = min(1, simpleData.percentage);
    simpleData.alertLevel = floor(simpleData.percentage * 5.9999);
    return simpleData;
  }
};
export default SimpleVersion;
